import clsx from 'clsx';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SharedButton from '@/global/Button/Button';

interface MyScoreInlineContentProps {
  myScoreDraft: Array<{ name: string; score: number }>;
  myScoreThreshold: number;
  isMyScoreSourcePickerOpen: boolean;
  availableSourcesToAdd: string[];
  thresholdAdjustedSources: boolean;
  myScoreLimitAdjusted: boolean;
  onToggleSourcePicker: () => void;
  onAddSource: (sourceName: string) => void;
  onRemoveSource: (sourceName: string) => void;
  onSliderChange: (sourceName: string, newScore: number) => void;
  onThresholdChange: (nextThreshold: number) => void;
  onApply: () => void;
  onClearWeights?: () => void;
  getSliderTrackStyle: (value: number) => { background: string };
}

export function MyScoreInlineContent({
  myScoreDraft,
  myScoreThreshold,
  isMyScoreSourcePickerOpen,
  availableSourcesToAdd,
  thresholdAdjustedSources,
  myScoreLimitAdjusted,
  onToggleSourcePicker,
  onAddSource,
  onRemoveSource,
  onSliderChange,
  onThresholdChange,
  onApply,
  onClearWeights,
  getSliderTrackStyle,
}: MyScoreInlineContentProps) {
  return (
    <div className="space-y-3 py-1 min-w-0">
      <style>
        {`
          .my-score-slider {
            -webkit-appearance: none;
            appearance: none;
            outline: none;
          }
          .my-score-slider:focus {
            outline: none;
            box-shadow: none;
          }
          .my-score-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 999px;
            background: #ffffff;
            border: 3px solid #be0080;
            box-shadow: none;
            cursor: pointer;
          }
          .my-score-slider::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 999px;
            background: #ffffff;
            border: 3px solid #be0080;
            box-shadow: none;
            cursor: pointer;
          }
        `}
      </style>
      <div className="relative">
        <button
          type="button"
          onClick={onToggleSourcePicker}
          className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:border-gray-300"
        >
          <span>
            {myScoreDraft.length > 0
              ? `${myScoreDraft.length} source${myScoreDraft.length > 1 ? 's' : ''} selected`
              : 'Select sources'}
          </span>
          <ChevronDownIcon
            className={clsx('h-4 w-4 transition-transform', isMyScoreSourcePickerOpen && 'rotate-180')}
          />
        </button>
        {isMyScoreSourcePickerOpen && (
          <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-44 overflow-auto rounded-xl border border-gray-200 bg-white p-1 shadow-lg">
            {availableSourcesToAdd.length === 0 ? (
              <p className="px-2 py-2 text-xs text-gray-500">All sources selected</p>
            ) : (
              availableSourcesToAdd.map(sourceName => (
                <button
                  key={sourceName}
                  type="button"
                  onClick={() => onAddSource(sourceName)}
                  className="block w-full rounded-md px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  {sourceName.replace(/_/g, ' ')}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {myScoreDraft.length > 0 && (
        <div className="rounded-xl border border-pink-200 bg-pink-50/40 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-800">Score threshold</span>
            <span className="rounded-md border border-pink-300 bg-pink-100 px-2 py-0.5 text-xs font-semibold text-pink-600">
              {'>='} {myScoreThreshold.toFixed(2)}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={myScoreThreshold}
            onChange={e => onThresholdChange(Number(e.target.value))}
            className="my-score-slider w-full h-2 cursor-pointer rounded-full"
            style={getSliderTrackStyle(myScoreThreshold)}
          />
          {thresholdAdjustedSources && (
            <p className="mt-2 text-xs text-gray-400">
              Moving the threshold will redistribute the weights of the sources equally.
            </p>
          )}
        </div>
      )}

      <div className="px-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Source weights</p>
        <div className="space-y-2">
          {myScoreDraft.length === 0 ? (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
              No sources selected yet.
            </p>
          ) : (
            myScoreDraft.map(source => (
              <div key={source.name}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="truncate text-sm text-gray-800">{source.name.replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-500">{source.score.toFixed(2)}</span>
                    <button
                      type="button"
                      onClick={() => onRemoveSource(source.name)}
                      className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                      title="Remove source"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={source.score}
                  onChange={e => onSliderChange(source.name, Number(e.target.value))}
                  className="my-score-slider w-full h-2 cursor-pointer rounded-full"
                  style={getSliderTrackStyle(source.score)}
                />
              </div>
            ))
          )}
          {myScoreLimitAdjusted && (
            <p className="mt-2 text-xs text-gray-400">
              The total score cannot exceed 1.00. The value was adjusted automatically.
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1 justify-between">
        <SharedButton type="button" variant="secondaryOutline" size="xs" onClick={onApply}>
          Apply weights
        </SharedButton>
        {onClearWeights && (
          <SharedButton type="button" variant="secondaryText" size="xs" onClick={onClearWeights}>
            Clear
          </SharedButton>
        )}
      </div>
    </div>
  );
}
